import React, { useRef, useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Box } from "@mui/material";
import { Check as CheckIcon } from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";

// ─── Urgency option config ──────────────────────────────────

interface UrgencyOption {
  value: number;
  labelKey: string;
  dotColor: string;
}

const URGENCY_OPTIONS: UrgencyOption[] = [
  { value: 1, labelKey: "serviceReports.urgency.critical", dotColor: "#DC2626" },
  { value: 2, labelKey: "serviceReports.urgency.high", dotColor: "#E8A817" },
  { value: 4, labelKey: "serviceReports.urgency.normal", dotColor: "#3B82F6" },
  { value: 0, labelKey: "serviceReports.urgency.info", dotColor: "#8C9099" },
];

// ─── Props ──────────────────────────────────────────────────

interface UrgencyPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  currentUrgency: number;
  onSelect: (urgency: number) => void;
  onClose: () => void;
  translate: (key: string) => string;
}

// ─── Component ──────────────────────────────────────────────

const UrgencyPopover: React.FC<UrgencyPopoverProps> = ({
  anchorEl,
  open,
  currentUrgency,
  onSelect,
  onClose,
  translate,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // Calculate menu position from anchor element
  const updatePosition = useCallback(() => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 4,
      left: rect.left,
    });
  }, [anchorEl]);

  useEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        anchorEl &&
        !anchorEl.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, anchorEl, onClose]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Close on scroll
  useEffect(() => {
    if (!open) return;
    const handler = () => onClose();
    window.addEventListener("scroll", handler, true);
    return () => window.removeEventListener("scroll", handler, true);
  }, [open, onClose]);

  if (!open || !anchorEl) return null;

  // Normalize current urgency: values not in 1,2,4 map to Info (0)
  const normalizedCurrent =
    currentUrgency === 1 || currentUrgency === 2 || currentUrgency === 4
      ? currentUrgency
      : 0;

  return createPortal(
    <Box
      ref={menuRef}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      sx={{
        position: "fixed",
        top: `${menuPos.top}px`,
        left: `${menuPos.left}px`,
        zIndex: 1300,
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)",
        minWidth: "170px",
        py: "6px",
        animation: "urgencyPopoverIn 0.15s ease-out",
        "@keyframes urgencyPopoverIn": {
          "0%": { opacity: 0, transform: "scale(0.95) translateY(-4px)" },
          "100%": { opacity: 1, transform: "scale(1) translateY(0)" },
        },
      }}
    >
      {URGENCY_OPTIONS.map((opt) => {
        const isSelected = normalizedCurrent === opt.value;

        return (
          <Box
            key={opt.value}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onSelect(opt.value);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              px: "14px",
              py: "9px",
              cursor: "pointer",
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: isSelected ? 600 : 500,
              color: isSelected ? "#0C1117" : "#3D3D3D",
              transition: "all 0.12s ease",
              "&:hover": {
                backgroundColor: "rgba(212,87,43,0.06)",
              },
            }}
          >
            {/* Colored dot */}
            <Box
              sx={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: opt.dotColor,
                flexShrink: 0,
              }}
            />

            {/* Label */}
            <Box sx={{ flex: 1 }}>
              {translate(opt.labelKey)}
            </Box>

            {/* Checkmark for current selection */}
            {isSelected && (
              <CheckIcon
                sx={{
                  fontSize: "16px",
                  color: "#0C1117",
                  flexShrink: 0,
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>,
    document.body,
  );
};

export default UrgencyPopover;
